import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, AlertOptions, LoadingController, LoadingOptions, ToastController, ToastOptions } from "@ionic/angular";
import { Observable, from } from "rxjs";

@Injectable({ providedIn: 'root' })
export class NotificationService {
    private loadingController = inject(LoadingController);
    private alertController = inject(AlertController);
    private toastController = inject(ToastController);
    private router = inject(Router);

    private loaders: HTMLIonLoadingElement[] = [];

    presentLoading(options: LoadingOptions): Observable<HTMLIonLoadingElement> {
        const defaultedOptions = {
            message: 'Please Wait',
            duration: 30000,
            ...options
        };

        return new Observable((observer) => {
            from(this.loadingController.create(defaultedOptions)).subscribe((loader) => {
                this.loaders.push(loader);
                loader.present();
                observer.next(loader);
                observer.complete();

                this.handleLoaderDismiss(loader);
            });
        });
    }

    dismissLoaderById(id: string) {
        this.loadingController.dismiss({ manualDismiss: true }, '', id);
    }

    dismissLoader(loader: HTMLIonLoadingElement) {
        loader.dismiss({ manualDismiss: true });
    }

    dismissAllLoaders() {
        while (this.loaders.length > 0) {
            this.dismissLoader(this.loaders.pop());
        }

        this.dismissAllLoadersLegacy();
    }

    private handleLoaderDismiss(loader: HTMLIonLoadingElement) {
        from(loader.onDidDismiss()).subscribe((event) => {
            const index = this.loaders.indexOf(loader);

            if (index > -1) {
                this.loaders.splice(index, 1);
            }

            const isManualDismiss = event?.data?.manualDismiss;
            const isLegacyDismiss = typeof event?.data === 'boolean' && event?.data === true;

            if (!isManualDismiss && !isLegacyDismiss) {
                this.presentTimeoutAlert();
            }
        });
    }

    private presentTimeoutAlert() {        
        this.presentAlert({
            header: 'Long Request',
            message: 'The request is taking longer than expected. The request will continue in the background.',
            buttons: [
                { text: 'Close', handler: () => this.dismissAllLoaders() }
            ],
        }).subscribe();
    }

    dismissAllLoadersLegacy() {
        setTimeout(async () => {
            let topLoader = await this.loadingController.getTop();

            while (topLoader) {
                if (!(await topLoader.dismiss({ manualDismiss: true }))) {
                    return;
                }
                topLoader = await this.loadingController.getTop();
            }
        });
    }

    presentToast(options: ToastOptions) {
        const defaultedOptions = {
            header: '',
            message: '',
            color: 'success',
            duration: 2000,
            position: 'bottom' as 'bottom',
            keybordClose: true,
            buttons: [{ text: 'Done', role: 'cancel', handler: () => { } }],
            ...options
        };

        return new Observable((observer) => {
            from(this.toastController.create(defaultedOptions))
                .subscribe((toast) => {
                    toast.present();
                    observer.next(toast);
                    observer.complete();
                });
        });
    }

    presentAlert(options: AlertOptions) {
        const defaultedOptions = {
            header: '',
            subHeader: '',
            message: '',
            buttons: ['OK'],
            backdropDismiss: false,
            ...options
        }

        return new Observable<HTMLIonAlertElement>((observer) => {
            from(this.alertController.create(defaultedOptions))
                .subscribe((alert) => {
                    alert.present();
                    observer.next(alert);
                    observer.complete();
                });
        });
    }
}